import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { STROKES, type StrokePattern } from "../../data/games";
import { AppHeader, Icon } from "../common/ui";

interface Props {
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

const VW = 100, VH = 60;
const BEST_KEY = "bee_stroke_best";

type Pt = [number, number];

// Densify a polyline into ~1-unit-spaced points for distance/coverage maths.
function densify(path: Pt[], step = 1.2): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const [x1, y1] = path[i], [x2, y2] = path[i + 1];
    const d = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(1, Math.round(d / step));
    for (let k = 0; k < n; k++) out.push([x1 + ((x2 - x1) * k) / n, y1 + ((y2 - y1) * k) / n]);
  }
  out.push(path[path.length - 1]);
  return out;
}

function loadBest(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(BEST_KEY) || "{}"); } catch { return {}; }
}

export default function StrokeTrainer({ onExit, theme, onThemeChange }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const pick = (b: { ru: string; en: string }) => (isRu ? b.ru : b.en || b.ru);

  const [patternIdx, setPatternIdx] = useState(0);
  const pattern: StrokePattern = STROKES[patternIdx];
  const guide = useMemo(() => densify(pattern.path), [pattern]);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [userPts, setUserPts] = useState<Pt[]>([]);
  const [score, setScore] = useState<number | null>(null);
  const [best, setBest] = useState<Record<string, number>>(loadBest);

  const toView = (clientX: number, clientY: number): Pt => {
    const r = svgRef.current!.getBoundingClientRect();
    return [((clientX - r.left) / r.width) * VW, ((clientY - r.top) / r.height) * VH];
  };

  const start = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setScore(null);
    setDrawing(true);
    setUserPts([toView(e.clientX, e.clientY)]);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing) return;
    setUserPts((p) => [...p, toView(e.clientX, e.clientY)]);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setUserPts((pts) => { evaluate(pts); return pts; });
  };

  const evaluate = (pts: Pt[]) => {
    if (pts.length < 6) { setScore(0); return; }
    // average deviation of each user point to the nearest guide point
    let devSum = 0;
    for (const [ux, uy] of pts) {
      let min = Infinity;
      for (const [gx, gy] of guide) {
        const d = (ux - gx) ** 2 + (uy - gy) ** 2;
        if (d < min) min = d;
      }
      devSum += Math.sqrt(min);
    }
    const avgDev = devSum / pts.length;
    // coverage: share of guide points approached by the user
    let covered = 0;
    for (const [gx, gy] of guide) {
      for (const [ux, uy] of pts) {
        if ((ux - gx) ** 2 + (uy - gy) ** 2 <= 49) { covered++; break; }
      }
    }
    const coverage = covered / guide.length;
    const deviationScore = Math.max(0, Math.min(100, 100 - avgDev * 8));
    const coverageFactor = Math.max(0, Math.min(1, coverage / 0.8));
    const final = Math.round(deviationScore * (0.55 + 0.45 * coverageFactor));
    setScore(final);
    if (final > (best[pattern.key] ?? 0)) {
      const nb = { ...best, [pattern.key]: final };
      setBest(nb);
      try { localStorage.setItem(BEST_KEY, JSON.stringify(nb)); } catch { /* ignore */ }
    }
  };

  const clear = () => { setUserPts([]); setScore(null); };
  const selectPattern = (i: number) => { setPatternIdx(i); setUserPts([]); setScore(null); };

  const stars = score === null ? 0 : score >= 85 ? 3 : score >= 70 ? 2 : score >= 50 ? 1 : 0;
  const guideD = "M " + pattern.path.map(([x, y]) => `${x} ${y}`).join(" L ");
  const userD = userPts.length > 1 ? "M " + userPts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ") : "";
  const startPt = pattern.path[0];

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("games.stroke.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 620 }}>
        {/* Pattern selector */}
        <div className="wraprow" role="group" style={{ marginBottom: 14 }}>
          {STROKES.map((s, i) => (
            <button key={s.key} type="button" aria-pressed={i === patternIdx}
              className="pill" onClick={() => selectPattern(i)}>
              {pick(s.name)}
            </button>
          ))}
        </div>

        <p className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.45, marginBottom: 12 }}>
          {pick(pattern.hint)}
        </p>

        {/* Tracing canvas */}
        <div className="glass glass-lg" style={{ padding: 12 }}>
          <div style={{ position: "relative", width: "100%", aspectRatio: "5 / 3", touchAction: "none" }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VW} ${VH}`}
              preserveAspectRatio="none"
              width="100%" height="100%"
              style={{ display: "block", borderRadius: 12, background: "var(--glass-1)", touchAction: "none", cursor: "crosshair" }}
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
            >
              {/* guide path */}
              <path d={guideD} fill="none" stroke="var(--text-3)" strokeWidth={2}
                strokeDasharray="3 3" strokeLinecap="round" strokeLinejoin="round"
                vectorEffect="non-scaling-stroke" />
              {/* start marker */}
              <circle cx={startPt[0]} cy={startPt[1]} r={2.4} fill="var(--accent-fill)" stroke="var(--accent-ink)"
                strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
              {/* user trail */}
              {userD && (
                <path d={userD} fill="none"
                  stroke={score === null ? "var(--accent-text)" : stars >= 2 ? "var(--success)" : "var(--accent-text)"}
                  strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke" opacity={0.9} />
              )}
            </svg>
          </div>

          {/* Result / actions */}
          <div className="between" style={{ marginTop: 12, gap: 10 }}>
            <div className="row" style={{ gap: 6 }}>
              {score !== null ? (
                <>
                  <span className="row" style={{ gap: 2 }}>
                    {[0, 1, 2].map((i) => (
                      <span key={i} style={{ color: i < stars ? "var(--accent-fill)" : "var(--text-3)" }}>
                        <Icon name="sparkles" size={16} />
                      </span>
                    ))}
                  </span>
                  <span className="t-h">{score}%</span>
                </>
              ) : (
                <span className="t-sm muted">{t("games.stroke.draw")}</span>
              )}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {(best[pattern.key] ?? 0) > 0 && (
                <span className="badge badge-amber"><Icon name="trophy" size={12} /> {best[pattern.key]}%</span>
              )}
              <button className="btn btn-glass" onClick={clear} style={{ paddingInline: 14 }}>
                <Icon name="rotate" size={16} /> {t("games.stroke.retry")}
              </button>
            </div>
          </div>
        </div>

        <p className="t-xs muted-2" style={{ marginTop: 12, textAlign: "center" }}>
          {t("games.stroke.tip")}
        </p>
      </div>
    </div>
  );
}
